import { range, sortBy } from 'lodash';

import { compareDateTime } from 'util/dateTime';

import { HOURS_IN_DAY, MAX_HOURS_IN_DAY_VIEW } from '../DayView/constants';

function format(item, openTime) {
  const startTime = item.startAt < openTime ? openTime : item.startAt;
  const endTime =
    item.endAt < item.startAt ? HOURS_IN_DAY + item.endAt : item.endAt;

  return {
    id: item.id,
    type: item.type,
    // we calculate number of cells * 100% (cell size) + 1px for every cell right border
    left: `calc(${(startTime - openTime) * 100}% + ${startTime - openTime}px)`,
    width: `calc(${(endTime - startTime) * 100}% + ${endTime - startTime}px)`,
  };
}

export function convertToDecimalTime(dateString, overnight) {
  const time = new Date(dateString);
  const hours = time.getHours();
  const minutes = time.getMinutes();

  if (overnight) {
    return hours + minutes / 60 + HOURS_IN_DAY;
  }

  return hours + minutes / 60;
}

export function adjustOvernightClosingTime(businessClosingTime) {
  businessClosingTime += HOURS_IN_DAY;
  return businessClosingTime <= MAX_HOURS_IN_DAY_VIEW
    ? businessClosingTime
    : MAX_HOURS_IN_DAY_VIEW;
}

export const getDecimalTimeRange = ({ start, end, overnight }) => {
  const decimalStartAt = convertToDecimalTime(start);
  const decimalEndAt = convertToDecimalTime(end, overnight);

  if (decimalStartAt > decimalEndAt) {
    return [];
  }

  const roundedDecimalStartAt = Math.round(decimalStartAt);
  const roundedDecimalEndAt = Math.round(decimalEndAt);

  const adjustedStartAt =
    decimalStartAt < roundedDecimalStartAt
      ? roundedDecimalStartAt - 1
      : roundedDecimalStartAt;

  const adjustedEndAt =
    decimalEndAt > roundedDecimalEndAt
      ? roundedDecimalEndAt + 1
      : roundedDecimalEndAt;

  return range(adjustedStartAt, adjustedEndAt);
};

export const getOvernightShiftHour = shiftEndHour => {
  if (typeof shiftEndHour !== 'number') return;
  const overnightShiftHour = shiftEndHour + HOURS_IN_DAY;
  return overnightShiftHour <= MAX_HOURS_IN_DAY_VIEW
    ? overnightShiftHour
    : MAX_HOURS_IN_DAY_VIEW;
};

// Convert all types of resources into the same format before calling formatCollection
export const fullDayPeriod = ({
  id,
  type,
  startAt,
  endAt,
  isFullDay,
  openTime,
  closingTime,
}) => {
  if (isFullDay) {
    return {
      id,
      type,
      startAt: openTime,
      endAt: closingTime,
    };
  }
  const decimalStartTime = convertToDecimalTime(startAt);
  const decimalEndTime = convertToDecimalTime(endAt);
  const isOvernightShift = decimalStartTime > decimalEndTime;

  const endTime = closingTime < decimalEndTime ? closingTime : decimalEndTime;

  return {
    id,
    type,
    startAt: decimalStartTime,
    endAt: isOvernightShift ? getOvernightShiftHour(decimalEndTime) : endTime,
  };
};

export const getEarliestStartTime = shifts => {
  if (!shifts) return;
  shifts.sort((shiftA, shiftB) =>
    compareDateTime(shiftA.startAt, shiftB.startAt)
  );

  return shifts[0]?.startAt;
};

export const getLatestEndTime = shifts => {
  if (!shifts) return;
  shifts.sort((shiftA, shiftB) => compareDateTime(shiftA.endAt, shiftB.endAt));

  const shiftWithLatestEndTime = shifts.slice(-1);
  return shiftWithLatestEndTime[0]?.endAt;
};

// This method takes a collection of squares and tries to group them
// Find combinations to render multiple squares in one row that do not overlap with each other
// If they do overlap it puts them into different rows
export const formatCollection = (
  collection,
  openTime,
  closingTime,
  grouping
) => {
  let sortedCollection = sortBy(collection, ['startAt', 'endAt']).reverse();

  const result = [];
  let currentRow = [];
  let currentItem;

  while (sortedCollection.length) {
    // take an item from the collection
    currentItem = sortedCollection.pop();
    // format and put it to the current row
    currentRow.push(format(currentItem, openTime, closingTime));

    if (grouping) {
      const newCollection = [];

      // Iterate over all other items to see if anything else can be fit into this current row
      while (sortedCollection.length) {
        const item = sortedCollection.pop();

        // If we find one
        if (
          !(
            currentItem.startAt < item.endAt && currentItem.endAt > item.startAt
          )
        ) {
          // We push that square to the current row
          currentRow.push(format(item, openTime, closingTime));
          // Make this square "current" so during the next iteration we compare
          // the next square with this one already
          currentItem = item;
        } else {
          newCollection.push(item);
        }
      }

      // A collection with all squares that did not fit into this current row push
      // to the original collection to start over
      sortedCollection = newCollection;
    }

    result.push(currentRow); // push current row to the result array
    // Create a new row
    currentRow = [];
  }

  return result;
};
